/** Presentational analytics widgets — dependency-free SVG, matches the
 *  hand-rolled chart idiom already in src/components/. */
import type { MetricMap, ParetoRow, PerformanceScore } from "./types";
import { formatMetric, humanize } from "./format";

/** A card of key/value metric rows. */
export function MetricRows({ metrics }: { metrics: MetricMap }) {
  const entries = Object.entries(metrics);
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-baseline justify-between border-b border-nb-border/50 py-1">
          <dt className="text-sm text-nb-text-2">{humanize(k)}</dt>
          <dd className="font-mono text-sm text-nb-text">{formatMetric(k, v)}</dd>
        </div>
      ))}
    </dl>
  );
}

/** 0–100 semicircular gauge for the NexBasira Performance Score. */
export function ScoreGauge({ score, ariaLabel }: { score: PerformanceScore; ariaLabel: string }) {
  const value = score.score;
  const pct = value === null ? 0 : Math.max(0, Math.min(100, value)) / 100;
  const r = 80;
  const circ = Math.PI * r; // half circumference
  const dash = circ * pct;
  const color = value === null ? "#6B7384" : value >= 75 ? "#00E5A0" : value >= 50 ? "#A78BFA" : "#f59e0b";
  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 200 110" width="200" height="110" role="img" aria-label={ariaLabel}>
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke="#2A3344"
          strokeWidth="14"
          strokeLinecap="round"
        />
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke={color}
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
        />
        <text x="100" y="92" textAnchor="middle" className="fill-nb-text" style={{ fontSize: 32, fontWeight: 700 }}>
          {value === null ? "—" : Math.round(value)}
        </text>
      </svg>
      <p className="text-xs text-nb-text-3">
        {Math.round(score.weight_covered * 100)}% covered
      </p>
    </div>
  );
}

/** Horizontal-bar Pareto (rejection reasons / escalation reasons). */
export function ParetoBars({ rows, emptyLabel }: { rows: ParetoRow[]; emptyLabel: string }) {
  if (!rows.length) return <p className="text-sm text-nb-text-3">{emptyLabel}</p>;
  const max = Math.max(...rows.map((r) => r.count));
  return (
    <ul className="space-y-1.5">
      {rows.map((r) => (
        <li key={r.reason} className="text-sm">
          <div className="mb-0.5 flex justify-between">
            <span className="text-nb-text-2">{humanize(r.reason)}</span>
            <span className="font-mono text-nb-text">{r.count}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-nb-border/50">
            <div
              className="h-full rounded-full bg-nb-mint"
              style={{ width: `${max ? (r.count / max) * 100 : 0}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
